import React, { useState, useCallback } from 'react';
import { Download, Trash2, ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * MyDeviceDataPanel — klant zelfservice voor device-data.
 *
 * Functies:
 *  - Bekijken/exporteren van alle device-data als JSON download
 *  - Anonimiseren/verwijderen met expliciete bevestigingsstap
 *  - Betaal-/factuur-/accountdata wordt niet aangeraakt
 *
 * Gebruikt /api/customer/device-data/{export,delete}.
 */
export default function MyDeviceDataPanel({ sessionId }) {
  const [step, setStep] = useState('idle'); // idle | confirming | working | done | err
  const [err, setErr] = useState(null);
  const [exportSize, setExportSize] = useState(null);
  const [anonymizedId, setAnonymizedId] = useState(null);

  // Resolve a usable session id. Priority:
  //   1. explicit prop, 2. ps_session_id, 3. pilot/customer token-derived fallback,
  //   4. installed anonymous device id. Minimum length 8 chars to satisfy backend.
  let sid = sessionId || '';
  if (!sid && typeof window !== 'undefined') {
    try {
      sid = window.localStorage?.getItem('ps_session_id') || '';
      if (!sid) {
        const pilotTok = window.localStorage?.getItem('powersure_pilot_token') || '';
        const custTok = window.localStorage?.getItem('powersure_customer_token') || '';
        const seed = pilotTok || custTok;
        if (seed) {
          // derive a deterministic short id from the token (not the token itself)
          sid = 'mp-' + seed.split('').reduce((a, c) => ((a * 31 + c.charCodeAt(0)) | 0), 0)
            .toString(16).replace('-', 'n').padStart(8, '0');
          window.localStorage.setItem('ps_session_id', sid);
        }
      }
      if (!sid) {
        sid = 'mp-' + Math.random().toString(36).slice(2, 10);
        window.localStorage?.setItem('ps_session_id', sid);
      }
    } catch { /* localStorage may be disabled */ }
  }

  const logEvent = useCallback(async (event) => {
    try {
      await fetch(`${API}/track/funnel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event, session_id: sid || 'mp-device-data' }),
      });
    } catch { /* no-op */ }
  }, [sid]);

  const downloadExport = useCallback(async () => {
    if (!sid || sid.length < 8) {
      setErr('Geen geldige sessie gevonden. Log opnieuw in om uw data op te halen.');
      setStep('err');
      return;
    }
    setStep('working'); setErr(null);
    try {
      const r = await fetch(`${API}/customer/device-data/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sid, reason: 'customer self-service export' }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j?.detail || 'export_failed');
      const blob = new Blob([JSON.stringify(j, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `powersure-device-data-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setExportSize(blob.size);
      logEvent('device_data_export_downloaded');
      setStep('done');
    } catch (e) {
      setErr(e.message || 'export_failed');
      setStep('err');
    }
  }, [sid, logEvent]);

  const requestAnonymize = useCallback(async () => {
    if (!sid || sid.length < 8) {
      setErr('Sessie is te kort om veilig te anonimiseren. Log opnieuw in.');
      setStep('err');
      return;
    }
    setStep('working'); setErr(null);
    try {
      const r = await fetch(`${API}/customer/device-data/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sid, reason: 'customer self-service anonymize' }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j?.detail || 'anonymize_failed');
      setAnonymizedId(j.anonymized_session_id);
      logEvent('device_data_anonymized');
      setStep('done');
    } catch (e) {
      setErr(e.message || 'anonymize_failed');
      setStep('err');
    }
  }, [sid, logEvent]);

  return (
    <section
      className="rounded-2xl border border-white/10 p-5 space-y-4"
      style={{ background: 'linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
      data-testid="mp-my-device-data"
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 flex-shrink-0">
          <ShieldCheck className="w-4 h-4" />
        </span>
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
            Mijn device-data
          </h3>
          <p className="text-[12.5px] text-white/65 leading-snug mt-0.5">
            Bekijk, download of laat uw apparaatgegevens anonimiseren. U houdt zelf de controle.
          </p>
        </div>
      </div>

      <div
        className="rounded-xl border border-emerald-400/20 bg-emerald-500/[0.06] p-3 flex items-start gap-2"
        data-testid="mp-device-data-scope-note"
      >
        <ShieldCheck className="w-4 h-4 text-emerald-300 mt-0.5 flex-shrink-0" />
        <p className="text-[12px] text-white/75 leading-relaxed">
          Het gaat hier <span className="text-emerald-200 font-medium">alleen om device-data</span>:
          herkende apparaten, suggesties, simulaties en plannen.
          Betaal-, factuur- en accountgegevens worden <span className="text-emerald-200">niet</span> geraakt
          — die volgen hun eigen wettelijke bewaartermijn.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={downloadExport}
          disabled={step === 'working'}
          data-testid="mp-device-data-export-btn"
          className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-sky-500/15 hover:bg-sky-500/25 text-sky-200 text-sm font-semibold border border-sky-400/30 transition-colors disabled:opacity-60"
        >
          <Download className="w-4 h-4" />
          Mijn data downloaden (JSON)
        </button>
        <button
          type="button"
          onClick={() => setStep('confirming')}
          disabled={step === 'working'}
          data-testid="mp-device-data-anonymize-btn"
          className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-200 text-sm font-semibold border border-amber-400/30 transition-colors disabled:opacity-60"
        >
          <Trash2 className="w-4 h-4" />
          Mijn device-data verwijderen
        </button>
      </div>

      {step === 'confirming' && (
        <div
          className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-4 space-y-3"
          data-testid="mp-device-data-confirm"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
            <div className="space-y-1.5">
              <p className="text-sm font-semibold text-amber-100">Weet u dit zeker?</p>
              <p className="text-[12.5px] text-white/80 leading-relaxed">
                Uw apparaatherkenningen, suggesties, optimal plans en simulaties worden geanonimiseerd.
                U moet uw apparaten en advies daarna opnieuw opbouwen.
                <br />
                <span className="text-amber-200">Betaalgegevens en account blijven onaangetast.</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={requestAnonymize}
              data-testid="mp-device-data-confirm-yes"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-400 text-amber-950 text-xs font-semibold"
            >
              Ja, anonimiseer mijn device-data
            </button>
            <button
              type="button"
              onClick={() => setStep('idle')}
              data-testid="mp-device-data-confirm-no"
              className="px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white/65 text-xs border border-white/15"
            >
              Annuleren
            </button>
          </div>
        </div>
      )}

      {step === 'working' && (
        <div className="text-sm text-white/65" data-testid="mp-device-data-working">
          Bezig…
        </div>
      )}

      {step === 'done' && exportSize !== null && (
        <div
          className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3 text-sm text-emerald-100 inline-flex items-center gap-2"
          data-testid="mp-device-data-export-success"
        >
          <CheckCircle2 className="w-4 h-4 text-emerald-300" />
          Download gestart ({Math.round(exportSize / 1024)} KB).
        </div>
      )}

      {step === 'done' && anonymizedId && (
        <div
          className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3 text-sm text-emerald-100 space-y-1"
          data-testid="mp-device-data-anonymize-success"
        >
          <div className="inline-flex items-center gap-2 font-semibold">
            <CheckCircle2 className="w-4 h-4 text-emerald-300" /> Device-data geanonimiseerd
          </div>
          <p className="text-[12px] text-white/75">
            Uw account en betalingen zijn onaangetast. U kunt apparaten opnieuw toevoegen wanneer u wilt.
          </p>
        </div>
      )}

      {step === 'err' && (
        <div
          className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-3 text-sm text-amber-100"
          data-testid="mp-device-data-error"
        >
          {err}
        </div>
      )}
    </section>
  );
}
